import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../i18n/LanguageContext';
import { api } from '../../services/api';
import type { GarageDocumentResponse, TradeLicenseDownloadResponse } from '../../types/settings';
import UploadDocumentsModal from './UploadDocumentsModal';

export default function DocumentsTab() {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [license, setLicense] = useState<TradeLicenseDownloadResponse | null>(null);
  const [pendingLicense, setPendingLicense] = useState<GarageDocumentResponse | null>(null);
  const [emiratesId, setEmiratesId] = useState<GarageDocumentResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const loadDocuments = async () => {
    if (!token) return;
    try {
      const [activeLicense, docs] = await Promise.all([
        api.getTradeLicenseDownloadUrl(token).catch(() => ({ downloadUrl: null, licenseNumber: null, hasLicense: false })),
        api.listMyDocuments(token).catch(() => [] as GarageDocumentResponse[]),
      ]);
      setLicense(activeLicense);
      setPendingLicense(docs.find((d) => d.documentType === 'TRADE_LICENSE' && d.status === 'PENDING') || null);
      // Owner Emirates ID: pick the latest (newest) row regardless of status — partner sees its current state.
      const emiratesIdRows = docs
        .filter((d) => d.documentType === 'OWNER_EMIRATES_ID')
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
      setEmiratesId(emiratesIdRows[0] || null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDocuments(); }, [token]);

  const viewLicense = () => {
    if (license?.downloadUrl) {
      window.open(license.downloadUrl, '_blank');
    }
  };

  const viewEmiratesId = () => {
    if (emiratesId?.downloadUrl) {
      window.open(emiratesId.downloadUrl, '_blank');
    }
  };

  const emiratesIdStatusKey = (status: GarageDocumentResponse['status'] | undefined): string => {
    switch (status) {
      case 'APPROVED': return 'settings.ownerEmiratesId.approved';
      case 'PENDING': return 'settings.ownerEmiratesId.pending';
      case 'REJECTED': return 'settings.ownerEmiratesId.rejected';
      default: return 'settings.ownerEmiratesId.notUploaded';
    }
  };

  const emiratesIdStatusClass = (status: GarageDocumentResponse['status'] | undefined): string => {
    switch (status) {
      case 'APPROVED': return 'status-badge status-active';
      case 'PENDING': return 'status-badge status-pending';
      case 'REJECTED': return 'status-badge status-rejected';
      default: return 'status-badge';
    }
  };

  if (loading) return <div className="settings-tab-content"><p>{t('common.loading')}</p></div>;

  return (
    <div className="settings-tab-content">
      <div className="settings-section-header">
        <div />
        <button className="settings-edit-link" onClick={() => setShowModal(true)}>
          {t('settings.uploadReplace')} ✏️
        </button>
      </div>

      {!license?.hasLicense ? (
        <div className="settings-empty-state">
          <p>{t('settings.noDocuments')}</p>
          <button className="settings-btn-primary" onClick={() => setShowModal(true)}>
            {t('settings.uploadDocuments')}
          </button>
        </div>
      ) : (
        <div className="settings-data-list">
          <div className="settings-data-row">
            <span className="settings-data-label">{t('settings.tradeLicense')}</span>
            <span className="settings-data-value">
              <span className="status-badge status-active">{t('settings.tradeLicense.approved')}</span>
              <button className="settings-view-btn" onClick={viewLicense}>
                {t('settings.viewTradeLicense')}
              </button>
            </span>
          </div>
          <div className="settings-data-row">
            <span className="settings-data-label">{t('settings.tradeLicenseNumber')}</span>
            <span className="settings-data-value">{license.licenseNumber}</span>
          </div>

          {/* ─── Owner Emirates ID row ─── */}
          <div className="settings-data-row">
            <span className="settings-data-label">{t('settings.ownerEmiratesId')}</span>
            <span className="settings-data-value">
              <span className={emiratesIdStatusClass(emiratesId?.status)}>
                {t(emiratesIdStatusKey(emiratesId?.status))}
              </span>
              {emiratesId?.downloadUrl && (
                <button className="settings-view-btn" onClick={viewEmiratesId}>
                  {t('settings.viewOwnerEmiratesId')}
                </button>
              )}
            </span>
          </div>

          {emiratesId?.status === 'REJECTED' && emiratesId.rejectionReason && (
            <div className="settings-info-banner" style={{ marginTop: '0.5rem', background: '#fef2f2', borderColor: '#fecaca' }}>
              <strong style={{ color: '#991b1b' }}>{t('settings.ownerEmiratesId.rejected')}</strong>
              <p style={{ color: '#991b1b' }}>{emiratesId.rejectionReason}</p>
            </div>
          )}
        </div>
      )}

      {pendingLicense && (
        <div className="settings-info-banner" style={{ marginTop: '1rem' }}>
          <strong>{t('settings.tradeLicense.pendingTitle')}</strong>
          <p>{t('settings.tradeLicense.pendingBody')}</p>
        </div>
      )}

      {showModal && (
        <UploadDocumentsModal
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); loadDocuments(); }}
        />
      )}
    </div>
  );
}
